// Dependencies
import {
  Grid,
  Skeleton,
  Stack,
  Paper,
  Container,
  TableContainer,
  TableHead,
  TableCell,
  Table,
  TableBody,
  TableRow,
  MenuItem,
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";

// Styles
import { WatchListStyles } from "../dashboard/styles/Watchlist.styles";
import { SkeletonStyles } from "../dashboard/styles/Skeletons.styles";

export const ContainerSkeleton = (props: { height: string }) => {
  const { height } = props;
  return (
    <Grid
      container
      sx={{
        background: "white",
        height: "175px",
        borderRadius: "25px",
        padding: "10px 20px",
      }}>
      <Grid item xs={2} my={3}>
        <Skeleton variant="circular" width={"auto"} height={"100%"} />
      </Grid>
      <Grid item xs={10} pt={2} pl={1}>
        <Skeleton variant={"text"} width={"100%"} height={height} />
        <Skeleton variant={"text"} width={"100%"} height={height} />
      </Grid>
      <Grid item xs={12}>
        <Skeleton animation="wave" variant="rectangular" width={"100%"} height={height} />
      </Grid>
      <Grid item xs={12}>
        <Skeleton animation="wave" variant="rectangular" width={"100%"} height={height} />
      </Grid>
    </Grid>
  );
};

export const NewlyListedSkeleton = () => {
  return (
    <Stack direction="row" mt={2} spacing={2}>
      <Skeleton variant="circular" width={35} height={35} />
      <Skeleton variant="text" width={150} height={40} />
    </Stack>
  );
};

export const CoinPerformanceSkeleton = () => {
  return (
    <Paper sx={{ boxShadow: 0 }} elevation={2}>
      <Stack
        sx={{
          paddingBottom: { md: "16px", xs: "30px" },
          paddingTop: { md: "30px", xs: "30px" },
          paddingLeft: { md: "30px", xs: "30px" },
        }}
        direction="row"
        spacing={2}>
        <Skeleton variant="circular" width={40} height={40} />
        <Stack spacing={1}>
          <Skeleton variant="text" width={70} height={20} />
          <Skeleton variant="text" width={30} height={20} />
          <Skeleton variant="text" width={120} height={50} />
        </Stack>
      </Stack>

      <Skeleton
        animation="pulse"
        variant="rectangular"
        sx={{ borderRadius: "10px" }}
        height={515}
      />
    </Paper>
  );
};

export const CoinDetailsSkeleton = () => {
  return <Skeleton sx={{ borderRadius: 2 }} animation="pulse" variant="rectangular" height={400} />;
};

export const CoinHoldingsSkeleton = () => {
  return <Skeleton sx={{ borderRadius: 2 }} animation="pulse" variant="rectangular" height={250} />;
};

export const CoinProfileSkeleton = () => {
  return (
    <>
      <Skeleton variant="text" animation="pulse" height={30} width={200} sx={{ marginBottom: 2 }} />
      <Skeleton variant="rectangular" height={300} />
    </>
  );
};

export const CryptoNewsCarouselSkeleton = () => {
  return (
    <Container>
      <Skeleton animation="pulse" variant="rectangular" sx={{ borderRadius: 2 }} height={250} />
      <Skeleton animation="pulse" variant="text" width={100} height={30} />
      <Skeleton animation="pulse" variant="text" height={30} />
    </Container>
  );
};

export const DashboardCryptoNewsCarouselSkeleton = () => {
  return (
    <Stack direction={"row"} style={{ gridGap: "10px" }}>
      {Array(3)
        .fill(null)
        .map((_, index) => (
          <Skeleton
            key={index}
            animation="pulse"
            variant="rectangular"
            style={{ borderRadius: 12 }}
            height={400}
            width={400}
          />
        ))}
    </Stack>
  );
};

export const MarketUpdateSkelton = () => {
  const classes = SkeletonStyles();
  return (
    <Stack spacing={1} className={classes.hideOnMobile}>
      <Skeleton variant="text" width={300} />
    </Stack>
  );
};

export const MarketUpdateMobileSkelton = () => {
  const classes = SkeletonStyles();
  return (
    <Stack spacing={1} className={classes.hideOnDesktop}>
      <Skeleton variant="text" width={300} />
    </Stack>
  );
};

export const WatchListSkeleton = () => {
  const classes = WatchListStyles();
  const textWidth: number = 120;
  const textHeight: number = 20;
  return (
    <Paper square={false} sx={{ paddingTop: 3, borderRadius: "25px", boxShadow: 0 }}>
      <Skeleton
        variant="text"
        animation="pulse"
        height={40}
        width={150}
        sx={{ marginBottom: 2, marginLeft: 3 }}
      />
      <Stack
        sx={{
          paddingBottom: { md: "16px", xs: "30px" },
          paddingTop: { md: "30px", xs: "30px" },
          paddingLeft: { md: "30px", xs: "30px" },
        }}
        direction="row"
        spacing={2}>
        <>
          <Skeleton variant="circular" width={40} height={40} />
          <Stack
            className={classes.hideOnMobile}
            justifyContent="space-between"
            alignItems="center"
            spacing={8}
            direction="row">
            <Skeleton variant="text" width={textWidth} height={textHeight} />
            <Skeleton variant="text" width={textWidth} height={textHeight} />
            <Skeleton variant="text" width={textWidth} height={textHeight} />
            <Skeleton variant="text" width={textWidth} height={textHeight} />
          </Stack>
          <Stack
            className={classes.hideOnDesktop}
            justifyContent="space-between"
            alignItems="center"
            spacing={50}
            direction="row">
            <Skeleton variant="text" width={200} height={textHeight} />
          </Stack>
        </>
      </Stack>
    </Paper>
  );
};

export const TargetOrderHoldingsSkeleton = () => {
  return (
    <Box
      sx={{
        display: "flex",
        gap: "10px",

        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "10px",
      }}>
      <Skeleton sx={{ borderRadius: "10px" }} variant="rectangular" width={210} height={60} />
      <Skeleton sx={{ borderRadius: "10px" }} variant="rectangular" width={210} height={60} />
      <Skeleton sx={{ borderRadius: "10px" }} variant="rectangular" width={210} height={60} />
    </Box>
  );
};

export const TreeMapSkelton = () => {
  return <Skeleton variant="rectangular" width={"100%"} height={600} />;
};

export const BasicTextSkeleton = () => {
  return <Skeleton variant="text" sx={{ fontSize: "3rem" }} />;
};

export const PriceAlertsTableSkelton = () => {
  const loadingSkeleton = [1, 2, 3, 4, 5];
  return (
    <TableContainer sx={{ width: " 100%", display: "flex", justifyContent: "center" }}>
      <Table sx={{ minWidth: 650, fontWeight: 600 }} aria-label="simple table">
        <TableHead>
          <TableRow>
            <TableCell>Coin</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Price Alert</TableCell>
            <TableCell>Current Price</TableCell>
            <TableCell>Difference</TableCell>
            <TableCell>Edit</TableCell>
            <TableCell>Delete</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loadingSkeleton.map((x, i) => {
            return (
              <TableRow key={i}>
                <TableCell
                  key={i}
                  style={{
                    position: "sticky",
                    left: "0px",
                    width: "10px",
                  }}
                  component="th"
                  scope="row">
                  <Skeleton variant="circular" width={40} height={40} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                </TableCell>
                <TableCell align="right">
                  <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                </TableCell>
                <TableCell align="right">
                  <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
// Pass a number on the amount of skeletons you need
interface IDropDownSkeleton {
  num: number;
}

export const DropDownSkeleton = (props: IDropDownSkeleton) => {
  return (
    <>
      {Array(props.num)
        .fill(null)
        .map((_, index) => (
          <MenuItem key={index}>
            <Skeleton sx={{ width: "100%" }} variant="text" />
          </MenuItem>
        ))}
    </>
  );
};

export const ManageAddressSkeleton = () => {
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (isMobile) {
    return (
      <Table>
        <TableBody>
          {Array(7)
            .fill(null)
            .map((_, index) => (
              <TableRow sx={{ borderBottom: "none" }}>
                <TableCell sx={{ width: "20px", paddingRight: "0", borderBottom: "none" }}>
                  <Skeleton animation="wave" variant="circular" width={35} height={35} />
                </TableCell>
                <TableCell sx={{ paddingRight: "20%", borderBottom: "none" }}>
                  <Skeleton
                    animation="wave"
                    variant="text"
                    sx={{ fontSize: "1rem", width: "100%" }}
                  />
                  <Skeleton
                    animation="wave"
                    variant="text"
                    sx={{ fontSize: "1rem", width: "100%" }}
                  />
                </TableCell>
                <TableCell
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    paddingLeft: "20%",
                    borderBottom: "none",
                  }}>
                  <Skeleton
                    animation="wave"
                    variant="text"
                    sx={{ fontSize: "1rem", width: "75%" }}
                  />
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <TableContainer
      sx={{
        overflowX: "scroll",
        "&::-webkit-scrollbar": {
          display: "none",
        },
      }}>
      <Table sx={{ minWidth: "1050px" }}>
        <TableBody>
          <TableRow>
            <TableCell sx={{ borderBottom: "none" }}>
              <Skeleton variant="text" sx={{ fontSize: "1rem", width: "50px" }} />
            </TableCell>
            <TableCell sx={{ borderBottom: "none", paddingLeft: "0" }}>
              <Skeleton variant="text" sx={{ fontSize: "1rem", width: "50px" }} />
            </TableCell>
            <TableCell sx={{ borderBottom: "none", paddingLeft: "30px" }}>
              <Skeleton variant="text" sx={{ fontSize: "1rem", width: "50px" }} />
            </TableCell>
            <TableCell sx={{ borderBottom: "none", paddingLeft: "30px" }}>
              <Skeleton variant="text" sx={{ fontSize: "1rem", width: "50px" }} />
            </TableCell>
            <TableCell sx={{ borderBottom: "none" }}>
              <Skeleton variant="text" sx={{ fontSize: "1rem", width: "50px" }} />
            </TableCell>
            <TableCell sx={{ borderBottom: "none" }}>
              <Skeleton variant="text" sx={{ fontSize: "1rem", width: "50px" }} />
            </TableCell>
            <TableCell sx={{ borderBottom: "none" }}>
              <Skeleton variant="text" sx={{ fontSize: "1rem", width: "50px" }} />
            </TableCell>
          </TableRow>
          {Array(4)
            .fill(null)
            .map((_, i) => {
              return (
                <TableRow key={i}>
                  <TableCell
                    sx={{
                      left: "0",
                      width: "0",
                      paddingRight: "0",
                      borderBottom: "none",
                    }}
                    component="th"
                    scope="row">
                    <Skeleton animation="wave" variant="circular" width={40} height={40} />
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none", paddingLeft: "0" }}>
                    <Skeleton
                      animation="wave"
                      variant="text"
                      sx={{ fontSize: "1rem", width: "50px" }}
                    />
                    <Skeleton
                      animation="wave"
                      variant="text"
                      sx={{ fontSize: "1rem", width: "50px" }}
                    />
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none", paddingLeft: "30px" }}>
                    <Skeleton
                      animation="wave"
                      variant="text"
                      sx={{ fontSize: "1rem", width: "50px" }}
                    />
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none", paddingLeft: "30px" }} width={"550px"}>
                    <Skeleton
                      animation="wave"
                      variant="text"
                      sx={{ fontSize: "1rem", width: "50px" }}
                    />
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none" }}>
                    <Skeleton
                      animation="wave"
                      variant="text"
                      sx={{ fontSize: "1rem", width: "50px" }}
                    />
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none" }}>
                    <Skeleton
                      animation="wave"
                      variant="text"
                      sx={{ fontSize: "1rem", width: "50px" }}
                    />
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none" }}>
                    <Skeleton
                      animation="wave"
                      variant="text"
                      sx={{ fontSize: "1rem", width: "50px" }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const WalletsSkeleton = () => {
  const loadingSkeleton = [1, 2, 3, 4, 5];
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (isMobile) {
    return (
      <Table>
        <TableBody>
          {Array(10)
            .fill(null)
            .map((_, index) => (
              <TableRow sx={{ borderBottom: "none" }}>
                <TableCell sx={{ width: "20px", paddingRight: "0", borderBottom: "none" }}>
                  <Skeleton animation="wave" variant="circular" width={35} height={35} />
                </TableCell>
                <TableCell sx={{ paddingRight: "20%", borderBottom: "none" }}>
                  <Skeleton
                    animation="wave"
                    variant="text"
                    sx={{ fontSize: "1rem", width: "100%" }}
                  />
                  <Skeleton
                    animation="wave"
                    variant="text"
                    sx={{ fontSize: "1rem", width: "100%" }}
                  />
                </TableCell>
                <TableCell
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    paddingLeft: "20%",
                    borderBottom: "none",
                  }}>
                  <Skeleton
                    animation="wave"
                    variant="text"
                    sx={{ fontSize: "1rem", width: "75%" }}
                  />
                  <Skeleton
                    animation="wave"
                    variant="text"
                    sx={{ fontSize: "1rem", width: "50%" }}
                  />
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <Table>
      <TableBody>
        <TableRow>
          <TableCell sx={{ borderBottom: "none" }}>
            <Skeleton variant="text" sx={{ fontSize: "1rem", width: "50px" }} />
          </TableCell>
          <TableCell sx={{ borderBottom: "none" }}>
            <Skeleton variant="text" sx={{ fontSize: "1rem", width: "50px" }} />
          </TableCell>
          <TableCell sx={{ borderBottom: "none" }}>
            <Skeleton variant="text" sx={{ fontSize: "1rem", width: "50px" }} />
          </TableCell>
          <TableCell sx={{ borderBottom: "none" }}>
            <Skeleton variant="text" sx={{ fontSize: "1rem", width: "50px" }} />
          </TableCell>
          <TableCell sx={{ borderBottom: "none" }}>
            <Skeleton variant="text" sx={{ fontSize: "1rem", width: "75px" }} />
          </TableCell>
          <TableCell sx={{ borderBottom: "none" }}>
            <Skeleton variant="text" sx={{ fontSize: "1rem", width: "50px" }} />
          </TableCell>
          <TableCell sx={{ borderBottom: "none" }}>
            <Skeleton variant="text" sx={{ fontSize: "1rem", width: "100px" }} />
          </TableCell>
          <TableCell sx={{ borderBottom: "none" }}>
            <Skeleton variant="text" sx={{ fontSize: "1rem", width: "100px" }} />
          </TableCell>
        </TableRow>
        {loadingSkeleton.map((x, i) => {
          return (
            <TableRow key={i}>
              <TableCell
                sx={{
                  position: "sticky",
                  left: "0px",
                  width: "10px",
                  borderBottom: "none",
                }}
                component="th"
                scope="row">
                <Skeleton animation="wave" variant="circular" width={40} height={40} />
              </TableCell>
              <TableCell sx={{ borderBottom: "none" }}>
                <Skeleton
                  animation="wave"
                  variant="text"
                  sx={{ fontSize: "1rem", width: "50px" }}
                />
                <Skeleton
                  animation="wave"
                  variant="text"
                  sx={{ fontSize: "1rem", width: "75px" }}
                />
              </TableCell>
              <TableCell sx={{ borderBottom: "none" }}>
                <Skeleton
                  animation="wave"
                  variant="text"
                  sx={{ fontSize: "1rem", width: "75px" }}
                />
              </TableCell>
              <TableCell sx={{ borderBottom: "none" }}>
                <Skeleton
                  animation="wave"
                  variant="text"
                  sx={{ fontSize: "1rem", width: "75px" }}
                />
              </TableCell>
              <TableCell sx={{ borderBottom: "none" }}>
                <Skeleton
                  animation="wave"
                  variant="text"
                  sx={{ fontSize: "1rem", width: "50px" }}
                />
              </TableCell>
              <TableCell sx={{ borderBottom: "none" }}>
                <Skeleton
                  animation="wave"
                  variant="text"
                  sx={{ fontSize: "1rem", width: "75px" }}
                />
              </TableCell>
              <TableCell sx={{ borderBottom: "none" }}>
                <Skeleton
                  animation="wave"
                  variant="text"
                  sx={{ fontSize: "1rem", width: "100px" }}
                />
              </TableCell>
              <TableCell sx={{ borderBottom: "none" }}>
                <Skeleton
                  animation="wave"
                  variant="text"
                  sx={{ fontSize: "1rem", width: "100px" }}
                />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export const MarketsTableSkeleton = () => {
  const loadingSkeleton = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  return (
    <tbody>
      {loadingSkeleton.map((x, i) => {
        return (
          <TableRow key={i}>
            <TableCell sx={{ paddingRight: "0" }}>
              <Skeleton animation="wave" variant="circular" width={20} height={20} />
            </TableCell>
            <TableCell sx={{ paddingRight: "0" }}>
              <Skeleton animation="wave" variant="text" sx={{ fontSize: "1rem", width: "25px" }} />
            </TableCell>
            <TableCell>
              <Skeleton animation="wave" variant="circular" width={40} height={40} />
            </TableCell>
            <TableCell>
              <Skeleton animation="wave" variant="text" sx={{ fontSize: "1rem" }} />
            </TableCell>
            <TableCell>
              <Skeleton animation="wave" variant="text" sx={{ fontSize: "1rem", width: "40px" }} />
            </TableCell>
            <TableCell>
              <Skeleton animation="wave" variant="text" sx={{ fontSize: "1rem", width: "40px" }} />
            </TableCell>
            <TableCell align="right">
              <Skeleton animation="wave" variant="text" sx={{ fontSize: "1rem", width: "40px" }} />
            </TableCell>
            <TableCell align="right">
              <Skeleton animation="wave" variant="text" sx={{ fontSize: "1rem", width: "75px" }} />
            </TableCell>
            <TableCell align="right">
              <Skeleton animation="wave" variant="text" sx={{ fontSize: "1rem", width: "75px" }} />
            </TableCell>
            <TableCell align="right">
              <Skeleton animation="wave" variant="text" sx={{ fontSize: "1rem", width: "75px" }} />
            </TableCell>
          </TableRow>
        );
      })}
    </tbody>
  );
};

export default function NewsSkeletons() {
  return (
    <Container>
      <Grid container padding="12px" marginBottom={"20px"} spacing={1}>
        <Grid item xs={12} sm={3}>
          <Skeleton variant="text" height={"40px"} />
          <Skeleton variant="rectangular" height={"60px"} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Skeleton variant="text" height={"40px"} />
          <Skeleton variant="rectangular" height={"60px"} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Skeleton variant="text" height={"40px"} />
          <Skeleton variant="rectangular" height={"60px"} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Skeleton variant="text" height={"40px"} />
          <Skeleton variant="rectangular" height={"60px"} />
        </Grid>
      </Grid>
    </Container>
  );
}
